import { Box, ClickAwayListener } from '@material-ui/core';
import { useAlgolia } from '@setvi/shared/hooks';
import { useCallback, useState } from 'react';
import { SSearchInput, SText } from '@setvi/shared/components';
import { TextColor, VALIDATORS } from '@setvi/shared/enums';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import SearchResults from './SearchResults';

// A resource name cannot be longer than this, so neither can a useful query;
// it also keeps a pasted block of text under Algolia's own limit.
const MAX_QUERY_LENGTH = VALIDATORS.NAME_MAX_LENGTH;

interface GlobalSearchProps {
  /** Width of the input and of the results panel under it. */
  width?: number;
}

/**
 * Algolia search over the company's resources, with its results in a panel
 * under the input.
 *
 * It used to live in the app header, where it showed on three routes and
 * searched resources from pages that had nothing to do with them. It now
 * belongs to the page that owns those resources (DEV-789).
 */
const GlobalSearch = ({ width = 520 }: GlobalSearchProps) => {
  const [query, setQuery] = useState<string>('');
  // Whether the results panel is shown. Separate from `query`: a click away
  // only hides the panel, the term stays in the input and focusing it again
  // shows the same results right away (DEV-944).
  const [open, setOpen] = useState<boolean>(false);
  const [isSearchingInsideDocuments, setIsSearchingInsideDocuments] =
    useState<boolean>(false);

  const { algoliaKey, sasToken } = useAppContext();
  const {
    searchAlgoliaResources,
    resources,
    isLoading,
    isResultInsideDocuments
  } = useAlgolia({ sasToken, algoliaKey, hitsPerPage: 50 });

  const tooLong = query.length > MAX_QUERY_LENGTH;

  const handleSearch = useCallback(
    (searchQuery: string) => {
      setQuery(searchQuery);
      setIsSearchingInsideDocuments(false);
      setOpen(true);
      // Over the limit the input turns red instead of searching.
      if (searchQuery.length > MAX_QUERY_LENGTH) return;
      searchAlgoliaResources(searchQuery, true, false);
    },
    [searchAlgoliaResources]
  );

  const handleSearchInsideDocuments = useCallback(
    (searchQuery: string) => {
      searchAlgoliaResources(searchQuery, false, true);
      setIsSearchingInsideDocuments(true);
    },
    [searchAlgoliaResources]
  );

  const handleSearchClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleFocus = useCallback(() => {
    setOpen(true);
  }, []);

  // An empty box shows its default state; the panel needs a term to search for.
  const showResults = open && !!query && !tooLong;

  return (
    <ClickAwayListener onClickAway={() => handleSearchClose()}>
      <Box
        position="relative"
        display="flex"
        flexDirection="column"
        width={width}
        zIndex={10}>
        <SSearchInput
          placeholder="Search resources..."
          width={width}
          iconPosition="start"
          loading={isLoading}
          defaultValue={query}
          error={tooLong}
          onChange={value => handleSearch(value)}
          onFocus={handleFocus}
        />

        {tooLong && (
          // Floats under the input so the header keeps its height.
          <Box position="absolute" top="100%" left={16} mt={0.5}>
            <SText
              title={`Search is limited to ${MAX_QUERY_LENGTH} characters.`}
              size="xs"
              weight="bold"
              fontColor={TextColor.Red}
            />
          </Box>
        )}

        {showResults && (
          <SearchResults
            resources={resources}
            isLoading={isLoading}
            searchQuery={query}
            isUserSearchingInsideDocuments={isSearchingInsideDocuments}
            onBackToQuickSearch={handleSearch}
            isResultInsideDocuments={isResultInsideDocuments}
            onSearchInsideDocuments={handleSearchInsideDocuments}
            handleSearchClose={handleSearchClose}
          />
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default GlobalSearch;
